import React, { useEffect, useRef, useState } from 'react';
import { InlineText } from '@ui';
/**
 * Inline text field for a staged upload row (Technician, Certification #).
 *
 * `InlineText`, matching the drawer's own Name cell, rather than a boxed `TextInput` — the row
 * should read as one set of inline fields, not a mix of inline and form controls.
 *
 * **Commits on blur, not on change.** A per-keystroke commit calls `updateItemMetadata`, which
 * replaces the queue item, re-renders `QueueTable`, and lets its autofocus effect pull the caret
 * back into the Name cell (autofocused since IP-16588) — so typing one letter moved focus out of
 * the field. Committing on blur means no state update while the user is typing at all.
 *
 * `InlineText`'s text variant is an UNCONTROLLED contentEditable (see `InlineEditBase`): React
 * never re-syncs its DOM text from a prop change after first render, so the value is seeded via
 * `children` and read back off the blur target. Same contract as `InlineNameCell`.
 */
export function QueueTextField({ itemId, value, disabled, placeholder, onCommit }) {
    const [currentValue, setCurrentValue] = useState(value);
    // Re-sync only when the value genuinely changes from outside (e.g. a retry resetting the row),
    // never from our own commit — which has already written the same value.
    const isCommittingRef = useRef(false);
    useEffect(() => {
        if (isCommittingRef.current)
            return;
        setCurrentValue(value);
    }, [value]);
    const handleBlur = (event) => {
        var _a;
        const target = event.currentTarget;
        const raw = ((_a = target.textContent) !== null && _a !== void 0 ? _a : '').trim();
        // Blurring without having edited anything must not push an update. The drawer's Name cell
        // carries the same guard for the same reason: an unconditional commit re-renders the row
        // between a neighbouring dropdown's mousedown and mouseup, so the dropdown opens and shuts
        // again and cannot be used (IP-16588).
        if (raw === currentValue) {
            target.textContent = currentValue;
            return;
        }
        isCommittingRef.current = true;
        setCurrentValue(raw);
        onCommit(raw);
        isCommittingRef.current = false;
    };
    return (React.createElement(InlineText, { key: itemId, width: "full", disableEdit: disabled, placeholder: placeholder, 
        /* No `label`: it prepends VISIBLE uneditable text (see InlineTextCell's note). */
        onBlur: handleBlur }, currentValue));
}
